/**
 * 右上角全局上传进度浮层（2026-10-22 用户需求）。
 *
 * 需求原话：「上传时最好是屏幕右上角有一个上传进度」。
 *
 * 为什么做全局浮层而不是给每个上传入口各加一条进度：
 *   用户反馈的场景是「一上传就出事 / 不知道传没传」，问题不在某个页面缺进度条，
 *   而在**上传这个动作全站不可见**。原来只有交付物卡片（拖拽队列）和产物附件区
 *   （按钮文案 + 细进度条）有进度，阶段任务卡、City 结论附件、评论附件、协作、
 *   财务导入等入口点完只能干等。若逐页补，永远是补不完的，且新增入口还会漏。
 *   所以在 axios 层统一接管（见 lib/api.js + lib/uploadProgress.js），这里只负责显示。
 *
 * 视觉口径（遵循 aligcus-pc-page Skill 基线）：
 *   · 与 sonner 提示卡同一右上角、同一水平对齐，但整体在提示卡**下方**错开，
 *     两者不会叠在一起；
 *   · 白色表面 + 轻边框 + 弱阴影，沿用项目浅色蓝紫体系，不引入新的品牌色；
 *   · 状态不只靠颜色表达：进行中显示百分比数字 + 进度条，完成显示「完成」文字，
 *     失败显示「失败」文字与原因；
 *   · 图标统一用 lucide-react。
 *
 * 交互取舍：
 *   · 上传本身不可「暂停」（HTTP 单请求），所以这里不给暂停/取消按钮 ——
 *     给了却按不动比没有更糟。需要取消的上传入口自己已有取消能力。
 *   · 浮层不拦截鼠标事件（pointer-events-none），不会挡住底下的页面操作。
 */
import { useEffect, useState } from "react";
import { ArrowUpCircle, CheckCircle2, AlertTriangle } from "lucide-react";
import { subscribeUploads } from "../lib/uploadProgress";
import { T } from "../lib/i18n";

/** 把字节数压成短标签，避免长文件名把卡片撑开。 */
const fmtSize = (n) => {
  const v = Number(n) || 0;
  if (v >= 1024 * 1024 * 1024) return `${(v / 1024 / 1024 / 1024).toFixed(1)} GB`;
  if (v >= 1024 * 1024) return `${(v / 1024 / 1024).toFixed(1)} MB`;
  if (v >= 1024) return `${Math.max(1, Math.round(v / 1024))} KB`;
  return `${v} B`;
};

const UploadProgressStack = () => {
  const [state, setState] = useState({ items: [] });
  /**
   * 让出提示卡占用的高度（像素）。
   *
   * 为什么需要动态测量：上传进度与 sonner 提示卡用的是同一个右上角锚点，
   * 上传失败时两者会同时出现 —— 实测直接叠在一起，进度卡把提示卡压住。
   * 固定留一段空白又会让没有提示时进度卡悬在半空，不像「右上角」。
   * 所以这里实时量一下当前可见提示卡的底边，只让出真实占用的高度；
   * 没有提示卡时 offset 为 0，进度卡就贴在右上角。
   *
   * 为什么用轮询而不是 MutationObserver：提示卡有进出场动画，
   * 观察 DOM 变动拿到的仍是动画中间值；250ms 采样一次足够跟手，
   * 且只在有上传任务时才跑，空闲时完全不占资源。
   */
  const [offsetPx, setOffsetPx] = useState(0);

  useEffect(() => subscribeUploads(setState), []);

  const visible = (state.items || []).length > 0;

  useEffect(() => {
    if (!visible) { setOffsetPx(0); return undefined; }
    // 与 Toaster 的 offset 基准保持一致：顶栏 4rem + 12px（见 App.js 的 Toaster offset）。
    const BASE_TOP = 4 * 16 + 12;
    let timer = 0;
    const tick = () => {
      let maxBottom = 0;
      document.querySelectorAll("[data-sonner-toast]").forEach((n) => {
        const r = n.getBoundingClientRect();
        // 隐藏 / 已移除的提示高度为 0，不计入。
        if (r.height > 0) maxBottom = Math.max(maxBottom, r.bottom);
      });
      const next = maxBottom > 0 ? Math.max(0, Math.round(maxBottom - BASE_TOP) + 8) : 0;
      setOffsetPx((cur) => (Math.abs(cur - next) > 1 ? next : cur));
      timer = window.setTimeout(tick, 250);
    };
    tick();
    return () => window.clearTimeout(timer);
  }, [visible]);

  // 没有任何任务时不渲染，避免在页面上留一个空容器。
  if (!visible) return null;

  return (
    <div
      className="pointer-events-none fixed right-4 z-[80] flex w-[min(20rem,calc(100vw-2rem))] flex-col gap-2"
      // 默认贴在右上角（与提示卡同一锚点）；有提示卡时按实测高度整体下移避让。
      style={{ top: `calc(4rem + env(safe-area-inset-top, 0px) + 12px + ${offsetPx}px)` }}
      data-testid="upload-progress-stack"
      aria-live="polite"
    >
      {state.items.map((it) => {
        const done = it.status === "done";
        const failed = it.status === "error";
        return (
          <div
            key={it.id}
            data-testid={`upload-progress-${it.id}`}
            data-status={it.status}
            data-pct={it.pct}
            className={`pointer-events-auto rounded-xl border bg-white/95 px-3 py-2 shadow-soft backdrop-blur ${failed ? "border-red-200" : done ? "border-emerald-200" : "border-slate-200"}`}
          >
            <div className="flex items-start gap-2">
              {failed ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" aria-hidden="true" />
                : done ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
                  : <ArrowUpCircle className="mt-0.5 h-4 w-4 shrink-0 text-violet-600" aria-hidden="true" />}
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <p className="min-w-0 flex-1 truncate text-xs font-semibold text-slate-800" title={it.label || T("上传中")}>
                    {it.label || T("文件上传")}
                  </p>
                  {/* 百分比用等宽数字，进度跳动时不左右抖 */}
                  <span className="shrink-0 text-[11px] font-bold tabular-nums text-slate-600">
                    {failed ? T("失败") : done ? T("完成") : `${it.pct}%`}
                  </span>
                </div>
                {/* 进度条：进行中用品牌紫；完成转绿；失败转红。颜色只是辅助，旁边始终有文字。 */}
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    className={`h-full rounded-full transition-[width] duration-200 ${failed ? "bg-red-400" : done ? "bg-emerald-500" : "bg-violet-500"}`}
                    style={{ width: `${failed ? 100 : Math.max(it.pct || 0, done ? 100 : 3)}%` }}
                  />
                </div>
                <p className="mt-1 truncate text-[10px] text-slate-500" title={failed ? it.error : undefined}>
                  {failed
                    ? (it.error || T("上传失败"))
                    : done
                      ? fmtSize(it.size)
                      : `${fmtSize(it.loaded)} / ${fmtSize(it.size)}`}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default UploadProgressStack;
